/**
 * Converts an array of property objects into a Record using a value mapping
 * function. Each property's `name` becomes a key in the resulting record.
 */
export function mapNamedProperties<P extends { name: string }, V>(
    properties: ReadonlyArray<P>,
    mapFn: (property: P) => V
): Record<string, V> {
    return flatMapProperties(properties, function* (p) {
        yield [p.name, mapFn(p)];
    });
}

/**
 * Converts an array of values into a Record using a flattening mapping
 * function. The function returns an Array of zero or more key-value pairs
 * for each item. It may also be a Generator that yields the same.
 *
 * Each key-value pair is added to the resulting record verbatim. This is
 * for more complex transformations that don't have a simple same-name
 * correspondence between properties and record keys.
 */
export function flatMapProperties<P, V>(
    properties: ReadonlyArray<P>,
    producerFn: (property: P) => Iterable<[string, V]>
): Record<string, V> {
    const rec: Record<string, V> = {};
    for (const p of properties) {
        for (const [k, v] of producerFn(p)) {
            rec[k] = v;
        }
    }
    return rec;
}
